import { PageViewEvent } from '@shopify/events';
import { onDocumentReady } from '@theme/utilities';

onDocumentReady(
  function dispatchPageViewEvent() {
    /** @type {HTMLElement | null} */
    const templateElement = document.querySelector('main[data-template]');
    const template = templateElement?.dataset.template || '';

    // -- Page --
    document.dispatchEvent(
      new PageViewEvent({
        page: {
          template,
          title: document.title,
          url: window.location.href,
        },
      })
    );
  },
  { once: true }
);

onDocumentReady(
  async function hydratePreOrdersFallback() {
    const section = document.querySelector('.apple-pre-orders');
    if (!section) return;

    const hasRenderedProducts = section.querySelector('.apple-pre-orders__grid a[href*="/products/"]');
    const emptyState = section.querySelector('.apple-pre-orders__empty');
    if (hasRenderedProducts || !emptyState) return;

    try {
      const response = await fetch('/collections/pre-orders/products.json?limit=4', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products.filter((product) => product?.handle).slice(0, 4) : [];
      if (products.length === 0) return;

      const grid = document.createElement('div');
      grid.className = 'apple-pre-orders__grid';
      grid.setAttribute('role', 'list');

      for (const product of products) {
        grid.append(buildPreOrderCard(product));
      }

      emptyState.replaceWith(grid);
      restorePreOrdersCta(section);
    } catch {
      // Keep the server-rendered empty state if the fallback endpoint is unavailable.
    }
  },
  { once: true }
);

function buildPreOrderCard(product) {
  const item = document.createElement('div');
  item.className = 'apple-pre-orders__item';
  item.setAttribute('role', 'listitem');

  const badge = document.createElement('span');
  badge.className = 'apple-pre-orders__badge';
  badge.textContent = 'Pre-order';
  item.append(badge);

  const card = document.createElement('a');
  card.className = 'apple-card apple-card--light apple-card--product-v2 apple-pre-order-product';
  card.href = `/products/${product.handle}`;

  const media = document.createElement('span');
  media.className = 'apple-card__media';
  media.style.aspectRatio = '1 / 1';

  const image = product.images?.[0];
  if (image?.src) {
    const img = document.createElement('img');
    img.className = 'apple-card__image';
    img.src = image.src;
    img.alt = image.alt || product.title || 'Pre-order product image';
    img.loading = 'lazy';
    img.sizes = '(min-width: 1100px) 25vw, (min-width: 750px) 50vw, 34vw';
    media.append(img);
  } else {
    const placeholder = document.createElement('span');
    placeholder.className = 'apple-card__placeholder';
    media.append(placeholder);
  }

  const content = document.createElement('span');
  content.className = 'apple-card__content apple-card__content--product';

  const title = document.createElement('span');
  title.className = 'apple-card__title';
  title.textContent = product.title || 'Pre-order product';
  content.append(title);

  const price = product.variants?.[0]?.price;
  if (price) {
    const priceRow = document.createElement('span');
    priceRow.className = 'apple-card__price-row';

    const priceText = document.createElement('span');
    priceText.className = 'apple-card__price';
    priceText.textContent = formatPreOrderPrice(price);

    priceRow.append(priceText);
    content.append(priceRow);
  }

  const actions = document.createElement('span');
  actions.className = 'apple-card__actions apple-card__actions--product';

  const button = document.createElement('span');
  button.className = 'apple-button apple-button--secondary apple-button--sm';
  button.textContent = 'View Product';

  actions.append(button);
  content.append(actions);

  card.append(media, content);
  item.append(card);

  return item;
}

function formatPreOrderPrice(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return value;

  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(numberValue);
}

function restorePreOrdersCta(section) {
  const disabledButton = section.querySelector('.apple-pre-orders__header-action .apple-button[aria-disabled="true"]');
  if (!disabledButton) return;

  const link = document.createElement('a');
  link.className = disabledButton.className;
  link.href = '/collections/pre-orders';
  link.textContent = disabledButton.textContent.trim() || 'View Pre-orders';
  disabledButton.replaceWith(link);
}
